import styled from 'styled-components'
import { Link } from 'react-router-dom'

import { Typography } from 'components/Typography/Typography'
import { IconTwitter } from 'components/Icons/IconTwitter'
import { IconGithub } from 'components/Icons/IconGithub'
import { IconDiscord } from 'components/Icons/IconDiscord'
import { IconLink } from 'components/Icons/IconLink'
import { useAppTheme } from 'contexts/ThemeProviderContext'
import { ROUTES } from 'utils/route-constants'

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: 1200px;
  width: 100%;
  margin: auto;
  align-items: flex-start;
  padding: 80px 0;

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
`

const SocialColumn = styled.div`
  width: 210px;

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    text-align: center;
  }
`

const SocialLinks = styled.div`
  display: flex;
  margin-top: 32px;

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    margin-top: 16px;
    justify-content: center;
    svg {
      width: 40px;
      height: 40px;
    }
  }
`

const CenterColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: auto;

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    display: none;
  }
`

const LogoImage = styled.img`
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    display: none;
  }
`

const MobileLogoImage = styled.img`
  @media screen and (min-width: ${p => p.theme.breakpoints.md}px) {
    display: none;
  }
`

const LegalLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
`

const MobileLegalLinks = styled(LegalLinks)`
  display: none;

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    display: flex;
    margin-top: 0;
  }
`

const LegalLink = styled(Link)`
  color: ${p => p.theme.colors.text.secondary};
  text-decoration: none;
  font-size: ${p => p.theme.typography.size.Caption1}px;
  font-family: ${p => p.theme.typography.family.Poppins};
  font-weight: ${p => p.theme.typography.weight.Medium};
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`

const LegalDivider = styled.span`
  color: ${p => p.theme.colors.text.secondary};
  font-size: ${p => p.theme.typography.size.Caption1}px;
`

const DocsColumn = styled.div`
  width: 250px;
  text-align: right;

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    text-align: center;
  }
`

const DocumentsButton = styled.a`
  cursor: pointer;
  text-decoration: none;
  background-color: ${p => p.theme.colors.button.background};
  color: ${p => p.theme.colors.text.primaryDarkBackground};
  border: none;
  padding: 12px 24px;
  border-radius: 9999px;
  font-size: ${p => p.theme.typography.size.Description}px;
  font-family: ${p => p.theme.typography.family.Poppins};
  font-weight: ${p => p.theme.typography.weight.Medium};
  display: flex;
  align-items: center;
  & > svg {
    margin-left: 8px;
  }
  width: max-content;
  transition: background-color 0.3s ease;
  &:hover {
    ${p => `background-color: ${p.theme.colors.button.hover};`}
  }

  margin-top: 32px;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    margin-top: 16px;
    font-size: ${p => p.theme.typography.size.Caption1}px;
    padding: 8px 16px;
    margin-left: auto;
    margin-right: auto;
  }

  @media screen and (min-width: ${p => p.theme.breakpoints.md}px) {
    margin-left: auto;
  }
`

const Divider = styled.div`
  margin: 32px 0;
  border: 1px solid ${p => p.theme.colors.divider};
  width: 220px;
  @media screen and (min-width: ${p => p.theme.breakpoints.md}px) {
    display: none;
  }
`

const MobileGap = styled.div`
  height: 80px;
  @media screen and (min-width: ${p => p.theme.breakpoints.md}px) {
    display: none;
  }
`

export const Footer = () => {
  const { theme } = useAppTheme()

  const logo = theme === 'dark' ? '/logo-title-header-dark.svg' : '/logo-title-header.svg'

  return (
    <>
      <Container>
        <MobileLogoImage src={logo} width="215" height="32" alt="KAI" />
        <Divider />
        <SocialColumn>
          <Typography variant="subtitle1">
            Follow us on <span style={{ fontWeight: 600 }}>social media</span>
          </Typography>
          <SocialLinks>
            <a
              href="https://x.com/kai_finance_sui"
              target="_blank"
              rel="noreferrer"
              style={{ display: 'flex', marginRight: 40 }}
            >
              <IconTwitter />
            </a>
            <a
              href="https://github.com/kunalabs-io"
              target="_blank"
              rel="noreferrer"
              style={{ marginRight: 40, display: 'flex' }}
            >
              <IconGithub />
            </a>
            <a href="https://discord.gg/XV2xYZjReX" target="_blank" rel="noreferrer" style={{ display: 'flex' }}>
              <IconDiscord />
            </a>
          </SocialLinks>
        </SocialColumn>
        <CenterColumn>
          <LogoImage src={logo} width="215" height="32" alt="KAI" />
          <LegalLinks>
            <LegalLink to={`/${ROUTES.TERMS}`}>Terms of Use</LegalLink>
            <LegalDivider>|</LegalDivider>
            <LegalLink to={`/${ROUTES.PRIVACY_POLICY}`}>Privacy Policy</LegalLink>
          </LegalLinks>
        </CenterColumn>
        <Divider />
        <DocsColumn>
          <Typography variant="subtitle1">
            Check out our <span style={{ fontWeight: 600 }}>documentation</span>
          </Typography>
          <DocumentsButton href="https://docs.kai.finance" target="_blank" rel="noreferrer">
            Docs <IconLink />
          </DocumentsButton>
        </DocsColumn>
        <Divider />
        <MobileLegalLinks>
          <LegalLink to={`/${ROUTES.TERMS}`}>Terms of Use</LegalLink>
          <LegalDivider>|</LegalDivider>
          <LegalLink to={`/${ROUTES.PRIVACY_POLICY}`}>Privacy Policy</LegalLink>
        </MobileLegalLinks>
      </Container>
      <MobileGap />
    </>
  )
}
